import React, { useState } from 'react';
import toast from 'react-hot-toast';
import {
    createMilestone, updateMilestone, deleteMilestone,
    type Milestone,
} from '../../packages/api-client/pm-api';
import { toDateInputValue } from '../../utils/date';
import { planErrorMessage } from './planData';

const STATUSES: { value: Milestone['status']; label: string }[] = [
    { value: 'upcoming', label: 'Upcoming' },
    { value: 'in_progress', label: 'In progress' },
    { value: 'completed', label: 'Completed' },
    { value: 'overdue', label: 'Overdue' },
];

const HEALTH_LABEL: Record<string, string> = {
    on_track: 'On track', late: 'Late', done: 'Done', no_target: 'No target date',
};

/**
 * Milestones inside Planning: create, edit date/status, delete — the same
 * milestone endpoints the project Overview uses — plus the schedule's
 * projected date and health for each.
 */
export function PlanMilestones({ projectId, milestones, milestoneHealth, onChanged }: {
    projectId: string;
    milestones: Milestone[];
    milestoneHealth?: Map<string, { health: string; projected?: string | null; floatDays?: number | null }>;
    onChanged: () => void;
}) {
    const [name, setName] = useState('');
    const [due, setDue] = useState('');
    const [adding, setAdding] = useState(false);
    // Due-date drafts per milestone, saved on blur (a date input fires change
    // on every keystroke of a typed date).
    const [dueDraft, setDueDraft] = useState<Record<string, string>>({});
    const [notice, setNotice] = useState<string | null>(null);

    function commitDue(m: Milestone) {
        const draft = dueDraft[m.id];
        setDueDraft(d => { const n = { ...d }; delete n[m.id]; return n; });
        if (draft === undefined || draft === toDateInputValue(m.due_date)) return;
        // PUT /milestones/{id} treats a missing/null due_date as "unchanged"
        // and cannot clear it, so a cleared field is not sent.
        if (!draft) { setNotice("A milestone's due date can be changed but not removed."); return; }
        setNotice(null);
        update(m, { due_date: draft }, 'change the due date');
    }

    async function add(e: React.FormEvent) {
        e.preventDefault();
        if (!name.trim()) return;
        setAdding(true);
        try {
            await createMilestone(projectId, { name: name.trim(), due_date: due || undefined, status: 'upcoming' });
            setName(''); setDue('');
            onChanged();
        } catch (err) {
            toast.error(planErrorMessage(err, 'add the milestone'));
        } finally {
            setAdding(false);
        }
    }

    async function update(m: Milestone, input: Partial<Milestone>, what: string) {
        try {
            await updateMilestone(m.id, input);
            onChanged();
        } catch (err) {
            toast.error(planErrorMessage(err, what));
        }
    }

    async function remove(m: Milestone) {
        if (!window.confirm(`Delete milestone "${m.name}"?`)) return;
        try {
            await deleteMilestone(m.id);
            onChanged();
        } catch (err) {
            toast.error(planErrorMessage(err, 'delete the milestone'));
        }
    }

    return (
        <section className="plan-milestones card" aria-labelledby="plan-milestones-heading">
            <h3 id="plan-milestones-heading" className="plan-subheading">Milestones ({milestones.length})</h3>
            {milestones.length === 0 ? (
                <p className="plan-muted">No milestones yet. Add one below to track a delivery date.</p>
            ) : (
                <ul className="plan-milestone-list">
                    {milestones.map(m => {
                        const h = milestoneHealth?.get(m.id);
                        return (
                            <li key={m.id} className="plan-milestone" data-testid={`plan-milestone-${m.id}`}>
                                <span className="plan-milestone-name">{m.name}</span>
                                <label className="plan-field">
                                    <span className="sr-only">Due date for {m.name}</span>
                                    <input type="date" className="form-input" value={dueDraft[m.id] ?? toDateInputValue(m.due_date)}
                                        onChange={e => setDueDraft(d => ({ ...d, [m.id]: e.target.value }))}
                                        onBlur={() => commitDue(m)}
                                        onKeyDown={e => { if (e.key === 'Enter') commitDue(m); }} />
                                </label>
                                <label className="plan-field">
                                    <span className="sr-only">Status for {m.name}</span>
                                    <select className="form-input" value={m.status}
                                        onChange={e => update(m, { status: e.target.value as Milestone['status'] }, 'change the status')}>
                                        {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                                    </select>
                                </label>
                                <span className="plan-muted plan-milestone-health">
                                    {h ? `${HEALTH_LABEL[h.health] ?? h.health}${h.projected ? ` · projected ${toDateInputValue(h.projected)}` : ''}` : ''}
                                </span>
                                <button type="button" className="btn btn-ghost btn-sm plan-danger" onClick={() => remove(m)}
                                    aria-label={`Delete milestone ${m.name}`}>Delete</button>
                            </li>
                        );
                    })}
                </ul>
            )}
            {notice && <p role="status" className="plan-muted">{notice}</p>}
            <form className="plan-milestone-add" onSubmit={add} aria-label="Add milestone">
                <label className="plan-field">
                    <span>Name</span>
                    <input className="form-input" value={name} onChange={e => setName(e.target.value)} required />
                </label>
                <label className="plan-field">
                    <span>Due date</span>
                    <input type="date" className="form-input" value={due} onChange={e => setDue(e.target.value)} />
                </label>
                <button type="submit" className="btn btn-secondary btn-sm" disabled={adding || !name.trim()}>Add milestone</button>
            </form>
        </section>
    );
}
